import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from './icons/CloseIcon'
import { WarningIcon } from './icons/WarningIcon'
import { ShieldIcon } from './icons/ShieldIcon'
import { useNavigate } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { type ConnectionHealth } from '../lib/types'
import { useNotifications, type NotificationItem } from '../context/NotificationsContext'
import { Toast } from './Toast'

type RetryState = 'idle' | 'loading' | 'success' | 'error'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  const now = Date.now()
  if (now - date.getTime() < 24 * 60 * 60 * 1000) {
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  }
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`
}


interface NotificationEntryProps {
  item: NotificationItem
  onAction: () => void
  onClose: () => void
  onStaleAdmin: () => void
}

function NotificationEntry({ item, onAction, onClose, onStaleAdmin }: NotificationEntryProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const inert = item.state !== 'active'
  const [retryState, setRetryState] = useState<RetryState>('idle')

  const mutate = (path: string, method: string) => {
    apiFetch(path, { method })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          onStaleAdmin()
        }
      })
      .finally(onAction)
  }

  const handleRetry = () => {
    if (retryState === 'loading' || item.entityId == null) return
    setRetryState('loading')
    apiFetch<ConnectionHealth>(`/api/v1/connections/${item.entityId}/health`)
      .then(result => {
        setRetryState(result.status === 'healthy' ? 'success' : 'error')
      })
      .catch(() => {
        setRetryState('error')
      })
  }

  const titleKey =
    item.type === 'unreachable_connection'
      ? 'notifications.unreachableConnection.title'
      : 'notifications.fingerprintChanged.title'
  const descKey =
    item.type === 'unreachable_connection'
      ? 'notifications.unreachableConnection.desc'
      : 'notifications.fingerprintChanged.desc'

  const isUnreachable = item.type === 'unreachable_connection'

  return (
    <div className={`notification-entry${inert ? ' notification-entry-inert' : ''}`}>
      <span
        className={`notification-entry-icon notification-entry-icon-${isUnreachable ? 'danger' : 'warning'}`}
        aria-hidden="true"
      >
        {isUnreachable ? <WarningIcon size={18} /> : <ShieldIcon />}
      </span>

      <div className="notification-entry-body">
        <div className="notification-entry-title">{t(titleKey)}</div>
        <div className="notification-entry-name">{item.entityName}</div>
        <div className="notification-entry-desc">{t(descKey)}</div>

        {retryState !== 'idle' && (
          <div className={`notification-retry-feedback notification-retry-feedback-${retryState === 'success' ? 'success' : 'error'}`}>
            {retryState === 'success' ? t('notifications.retryReachable') : t('notifications.retryUnreachable')}
          </div>
        )}

        <div className="notification-entry-actions">
          {isUnreachable ? (
            <>
              {retryState === 'loading' ? (
                <button type="button" className="btn btn-primary notification-action" disabled>
                  {t('notifications.retryChecking')}
                </button>
              ) : (
                (retryState === 'idle' || retryState === 'error') && (
                  <button
                    type="button"
                    className="btn btn-primary notification-action"
                    disabled={inert}
                    onClick={handleRetry}
                  >
                    {t('notifications.retry')}
                  </button>
                )
              )}
              <button
                type="button"
                className="btn btn-ghost notification-action"
                disabled={inert}
                onClick={() => { navigate('/global-settings/connections'); onClose() }}
              >
                {t('notifications.openSettings')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary notification-action"
                disabled={inert}
                onClick={() => mutate(`/api/v1/notifications/${item.id}/acknowledge`, 'POST')}
              >
                {t('notifications.acknowledge')}
              </button>
              <button
                type="button"
                className="btn btn-ghost notification-action"
                disabled={inert}
                onClick={() => mutate(`/api/v1/notifications/${item.id}/disable-connection`, 'POST')}
              >
                {t('notifications.disableConnection')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="notification-entry-top">
        <span className="notification-entry-time">{formatTimestamp(item.createdAt)}</span>
        <button
          type="button"
          className="notification-entry-dismiss"
          aria-label={t('notifications.dismiss')}
          title={t('notifications.dismiss')}
          disabled={inert || !isUnreachable}
          onClick={() => mutate(`/api/v1/notifications/${item.id}/dismiss`, 'POST')}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  )
}

function NotificationList({ onNavigate, onStaleAdmin }: Readonly<{ onNavigate?: () => void; onStaleAdmin?: () => void }>) {
  const { t } = useTranslation()
  const { notifications, refresh } = useNotifications()

  if (notifications.length === 0) {
    return <div className="notification-dropdown-empty">{t('notifications.empty')}</div>
  }
  return (
    <div className="notification-dropdown-list">
      {notifications.map(item => (
        <NotificationEntry
          key={item.id}
          item={item}
          onAction={refresh}
          onClose={onNavigate ?? (() => {})}
          onStaleAdmin={onStaleAdmin ?? (() => {})}
        />
      ))}
    </div>
  )
}

export function NotificationDropdown({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [staleAdminToast, setStaleAdminToast] = useState(false)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  function handleStaleAdmin() {
    setStaleAdminToast(true)
  }

  return (
    <div ref={ref} className="notification-dropdown" role="dialog" aria-label={t('notifications.title')}>
      <div className="notification-dropdown-header">
        <span>{t('notifications.title')}</span>
      </div>
      {staleAdminToast && (
        <Toast
          message={t('notifications.staleAdminError')}
          onDismiss={() => setStaleAdminToast(false)}
          variant="warning"
        />
      )}
      <NotificationList onNavigate={onClose} onStaleAdmin={handleStaleAdmin} />
    </div>
  )
}
